import { STATUS_LABEL } from "@/lib/constants";
import { fmtDateTime, fmtDate, fmtTime, fmtDayStr, ccsDayOf, money, PRICE_LABEL, deviceLabel } from "@/lib/auditDates";

const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

const CSS = `
@page { size: letter; margin: 10mm; }
* { box-sizing: border-box; }
body { font-family: Arial, sans-serif; color: #000; font-size: 9px; margin: 0; }
h1 { font-size: 14px; margin: 0 0 4px; }
.meta { display: flex; flex-wrap: wrap; gap: 4px 16px; margin-bottom: 6px; font-size: 9px; }
.meta b { font-weight: 700; }
.sum { display: flex; flex-wrap: wrap; gap: 4px 14px; border: 1px solid #000; padding: 4px 6px; margin-bottom: 8px; }
table { width: 100%; border-collapse: collapse; page-break-inside: auto; }
tr { page-break-inside: avoid; }
th, td { border: 1px solid #555; padding: 2px 3px; text-align: left; vertical-align: top; }
th { background: #e5e5e5; font-size: 8px; text-transform: uppercase; }
td.num { text-align: right; white-space: nowrap; }
.day { background: #f2f2f2; font-weight: 700; font-size: 10px; }
.box { width: 11px; height: 11px; border: 1px solid #000; display: inline-block; }
.muted { color: #444; font-size: 8px; }
tfoot td { font-weight: 700; }
`;

function shell(title, meta, summaryHtml, tableHtml) {
  const metaHtml = Object.entries(meta).map(([k, v]) => `<span><b>${esc(k)}:</b> ${esc(v)}</span>`).join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>${CSS}</style></head>
<body><h1>${esc(title)}</h1><div class="meta">${metaHtml}</div><div class="sum">${summaryHtml}</div>${tableHtml}</body></html>`;
}

const sumItem = (k, v) => `<span><b>${esc(k)}:</b> ${esc(v)}</span>`;

export function summaryItems(s, activity) {
  const items = [
    ["Reparaciones finalizadas (equipos)", s.finalized_devices],
    ["Órdenes con reparaciones finalizadas", s.finalized_orders],
    ["Producción", money(s.production_total)],
    ["No reparables", s.not_repairable],
    ["Rechazadas por cliente", s.client_rejected],
    ["Equipos retirados", s.picked_up_devices],
    ["Órdenes retiradas", s.picked_up_orders],
  ];
  if (activity) items.unshift(["Acontecimientos", s.events], ["Órdenes con actividad", s.orders_with_activity]);
  else items.unshift(["Registros listados", s.matched_rows], ["Órdenes únicas", s.matched_orders]);
  return items;
}

const DATE_KEY = { received: "received_at", finalized: "finalized_at", pickup: "pickup_at" };

export function buildRowsHtml(rows, meta, summary, dateField) {
  const key = DATE_KEY[dateField];
  const head = `<tr><th>#</th><th>Orden</th><th>Cliente</th><th>Equipo</th><th>Servicio</th><th>Técnico</th><th>Sucursal</th>
<th>Recepción</th><th>Finalización</th><th>Retiro</th><th>Estado</th><th>Precio</th><th>Verificado</th></tr>`;
  let body = "";
  let lastDay = null;
  rows.forEach((r, i) => {
    if (key) {
      const d = ccsDayOf(r[key]);
      if (d !== lastDay) { body += `<tr><td class="day" colspan="13">${esc(fmtDayStr(d))}</td></tr>`; lastDay = d; }
    }
    const eq = r.device_count > 1 ? ` — Eq. #${r.device_index}` : "";
    body += `<tr><td>${i + 1}</td><td>${esc(r.order_number)}${eq}</td><td>${esc(r.customer_name)}</td>
<td>${esc(deviceLabel(r))}</td><td>${esc(r.services || "—")}</td><td>${esc(r.technician_name || "Sin asignar")}</td>
<td>${esc(r.branch_name)}</td><td>${esc(fmtDate(r.received_at))}</td><td>${esc(r.finalized_at ? fmtDateTime(r.finalized_at) : "—")}</td>
<td>${esc(r.pickup_at ? fmtDateTime(r.pickup_at) : "—")}</td><td>${esc(STATUS_LABEL[r.status] || r.status)}</td>
<td class="num">${esc(r.price != null ? money(r.price) : "—")}<div class="muted">${esc(PRICE_LABEL[r.price_kind])}</div></td>
<td><span class="box"></span></td></tr>`;
  });
  const foot = `<tfoot><tr><td colspan="11">Total: ${rows.length} registros · Producción del período: ${esc(money(summary.production_total))}</td><td colspan="2"></td></tr></tfoot>`;
  const sumHtml = summaryItems(summary, false).map(([k, v]) => sumItem(k, v)).join("");
  return shell("Listado de órdenes — Auditoría", meta, sumHtml, `<table><thead>${head}</thead><tbody>${body}</tbody>${foot}</table>`);
}

export function buildActivityHtml(events, meta, summary) {
  const head = `<tr><th>Hora</th><th>Orden</th><th>Cliente</th><th>Equipo</th><th>Acción</th><th>Usuario</th><th>Técnico</th><th>Verificado</th></tr>`;
  let body = "";
  let lastDay = null;
  events.forEach((e) => {
    if (e.day !== lastDay) { body += `<tr><td class="day" colspan="8">${esc(fmtDayStr(e.day))}</td></tr>`; lastDay = e.day; }
    const eq = e.device_count > 1 ? ` — Eq. #${e.device_index}` : "";
    body += `<tr><td>${esc(fmtTime(e.at))}</td><td>${esc(e.order_number)}${eq}</td><td>${esc(e.customer_name)}</td>
<td>${esc(e.device_label)}</td><td>${esc(e.action)}<div class="muted">${esc(e.description)}</div></td>
<td>${esc(e.user_name)}</td><td>${esc(e.technician_name || "—")}</td><td><span class="box"></span></td></tr>`;
  });
  const sumHtml = summaryItems(summary, true).map(([k, v]) => sumItem(k, v)).join("");
  return shell("Actividad del período — Auditoría", meta, sumHtml, `<table><thead>${head}</thead><tbody>${body}</tbody></table>`);
}

export function openPrintWindow() {
  const w = window.open("", "_blank");
  if (w) w.document.write("<p style='font-family:Arial'>Generando listado…</p>");
  return w;
}

export function writeAndPrint(w, html) {
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
}
